import React, { useState, useMemo, useEffect } from 'react'
import useSWR from 'swr'
import dayjs from 'dayjs'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import type { Row } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import Loader from '@/components/ui/Loader'
import EmptyDatas from 'components/ui/EmptyDatas'
import Table from 'components/ui/my-data-table'
import MyPagination from 'components/ui/MyPagination'
import DisplayUpdateTime from 'ui/DisplayUpdateTime'
import { Button } from '@/components/ui/button'
import { DatePicker } from 'components/ui/date-picker'
import { ProductOptions } from 'config'
import Selector from 'components/ui/Selector'
import { cdnUrl } from 'commons/cdn'
import { MyImage } from 'components/MyImage'
import { MyMedia } from 'components/MyMedia'
import RedisKeyHint from './RedisKeyHint'

type AvatarListRow = {
  id: string
  url?: string
  thumb?: string
  engineWith?: string
  videoUrl?: string
  videoPreviewUrl?: string
  state?: number | null
  contentLang?: number | null
  simple?: number | null
  productIds?: number[] | null
  tid?: string
  updateTime?: number
  rankingScore?: number | null
}

type AvatarRedisListPage = {
  items: AvatarListRow[]
  hasMore: boolean
  pageEndId: string | null
  redisKey: string
  filterKey?: string
  asia: boolean
}

type ListKind = 'new' | 'daily' | 'simple'

type RegionCode = 'global' | 'asia'

const PAGE_SIZE = 50

const REGION_OPTIONS: { code: RegionCode; name: string }[] = [
  { code: 'global', name: 'Global' },
  { code: 'asia', name: 'Asia' },
]

/** 与 Worker MetricDateUtils 一致：UTC − 8h 业务日 */
function getMetricDailyDate(): Date {
  return dayjs().subtract(8, 'hour').startOf('day').toDate()
}

function avatarRedisKeys(
  kind: ListKind,
  pid: number,
  region: RegionCode,
  dateStr: string,
): string[] {
  const primary =
    kind === 'new'
      ? `avatar:list:new:pid${pid}`
      : kind === 'daily'
        ? `avatar:ranking:daily:reCreate:${dateStr}:pid${pid}`
        : `avatar:ranking:daily:reCreate:simple:${dateStr}:pid${pid}`

  if (region !== 'asia') {
    return [primary]
  }

  const filter =
    kind === 'new'
      ? 'avatar:list:new:asia'
      : `avatar:ranking:daily:reCreate:asia:${dateStr}`
  return [primary, filter]
}

const AvatarRedisLists = ({ kind }: { kind: ListKind }) => {
  const [pid, setPid] = useState(11)
  const [region, setRegion] = useState<RegionCode>('global')
  const [date, setDate] = useState(getMetricDailyDate)
  const [after, setAfter] = useState<string | null>(null)
  const [afters, setAfters] = useState<string[]>([])

  const asia = region === 'asia'
  const dateStr = dayjs(date).format('YYYY-MM-DD')
  const redisKeys = useMemo(
    () => avatarRedisKeys(kind, pid, region, dateStr),
    [kind, pid, region, dateStr],
  )

  const basePath =
    kind === 'new'
      ? '/template-lists/avatar-list-new'
      : kind === 'daily'
        ? '/template-lists/avatar-ranking-daily'
        : '/template-lists/avatar-ranking-daily-simple'

  const swrParams = useMemo(() => {
    const params: Record<string, string | number> = {
      limit: PAGE_SIZE,
      pid,
      asia: asia ? 1 : 0,
      ...(after ? { after } : {}),
    }
    if (kind !== 'new') {
      params.date = dateStr
    }
    return params
  }, [after, pid, asia, kind, dateStr])

  const { data, error, isLoading, mutate } = useSWR<AvatarRedisListPage>([
    basePath,
    swrParams,
  ])

  const goAfter = (next: string | null) => {
    setAfter(next)
    if (next === null) {
      setAfters([])
    } else {
      const index = afters.indexOf(next)
      if (index === -1) {
        setAfters([...afters, next])
      } else {
        setAfters(afters.slice(0, index + 1))
      }
    }
  }

  useEffect(() => {
    setAfter(null)
    setAfters([])
  }, [pid, region, kind, date])

  const nextAfter =
    data?.hasMore && data.pageEndId ? data.pageEndId : null

  const columns = useMemo(
    () => [
      {
        header: '#',
        cell: ({ row }: { row: Row<AvatarListRow> }) => row.index + 1,
      },
      ...(kind !== 'new'
        ? [
            {
              header: 'Score',
              id: 'rankingScore',
              cell: ({ row }: { row: Row<AvatarListRow> }) => {
                const score = row.original.rankingScore
                return score == null ? '-' : score
              },
            },
          ]
        : []),
      {
        header: 'Image',
        id: 'image',
        cell: ({ row }: { row: Row<AvatarListRow> }) => {
          const avatar = row.original
          const imageUrl = avatar.url ?? avatar.thumb
          const isSpine =
            avatar.engineWith === 'spine' &&
            Boolean(avatar.videoUrl || avatar.videoPreviewUrl)

          if (isSpine) {
            return (
              <MyMedia
                alt={avatar.id}
                className="aspect-square h-24 w-24 rounded-md border border-border bg-black"
                videoUrl={avatar.videoPreviewUrl || avatar.videoUrl}
                previewVideoUrl={avatar.videoUrl || avatar.videoPreviewUrl}
                imageUrl={imageUrl}
                lazyVideo
              />
            )
          }

          if (!imageUrl) return '-'
          return (
            <MyImage
              alt={avatar.id}
              src={cdnUrl(avatar.thumb ?? avatar.url)}
              previewSrc={cdnUrl(avatar.url ?? avatar.thumb)}
              className="aspect-square h-24 w-24 rounded-md border border-border bg-black object-contain"
            />
          )
        },
      },
      {
        header: 'Id',
        accessorKey: 'id',
        cell: ({ row }: { row: Row<AvatarListRow> }) => (
          <span className="font-mono text-xs">{row.original.id}</span>
        ),
      },
      {
        header: 'tid',
        accessorKey: 'tid',
        cell: ({ row }: { row: Row<AvatarListRow> }) =>
          row.original.tid || '-',
      },
      {
        header: 'State',
        id: 'state',
        cell: ({ row }: { row: Row<AvatarListRow> }) => {
          const state = row.original.state
          const online = state === 401
          return (
            <span
              className={
                online
                  ? 'text-green-600 font-medium'
                  : 'text-muted-foreground'
              }
            >
              {state ?? '-'}
            </span>
          )
        },
      },
      {
        header: 'contentLang',
        accessorKey: 'contentLang',
        cell: ({ row }: { row: Row<AvatarListRow> }) =>
          row.original.contentLang ?? '-',
      },
      {
        header: 'simple',
        accessorKey: 'simple',
        cell: ({ row }: { row: Row<AvatarListRow> }) =>
          row.original.simple ?? '-',
      },
      {
        header: 'productIds',
        id: 'productIds',
        cell: ({ row }: { row: Row<AvatarListRow> }) => {
          const ids = row.original.productIds
          if (!Array.isArray(ids) || ids.length === 0) return '-'
          return ids.join(',')
        },
      },
      {
        header: 'UpdateTime',
        accessorKey: 'updateTime',
        cell: ({ row }: { row: Row<AvatarListRow> }) => (
          <DisplayUpdateTime updateTime={row.original.updateTime} />
        ),
      },
    ],
    [kind],
  )

  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  })

  const filters = (
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <label className="shrink-0 text-sm font-medium">Pid:</label>
        <Selector
          datas={ProductOptions}
          labelName="Pid"
          value={pid}
          update={setPid}
          showCode
          width="w-[220px]"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="shrink-0 text-sm font-medium">Region:</label>
        <Selector
          datas={REGION_OPTIONS}
          labelName="Region"
          value={region}
          update={(v: RegionCode) => setRegion(v)}
          width="w-[160px]"
        />
      </div>
      {kind !== 'new' ? (
        <div className="flex items-center gap-2">
          <label className="shrink-0 text-sm font-medium">Date:</label>
          <DatePicker
            date={date}
            onSelect={(next: Date | undefined) => {
              if (next) {
                setDate(next)
              }
            }}
          />
        </div>
      ) : null}
      <RedisKeyHint keys={redisKeys} />
    </div>
  )

  if (isLoading && !data) {
    return (
      <div className="space-y-4">
        {filters}
        <Loader />
      </div>
    )
  }
  if (!data || error) {
    return (
      <div className="space-y-4">
        {filters}
        <EmptyDatas />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex-shrink-0 mb-4 space-y-2">
        <div className="flex flex-wrap items-center gap-4">
          <MyPagination
            nextAfter={nextAfter}
            after={after}
            goAfter={goAfter}
            afters={afters}
            className="w-auto m-0"
          />
          <Button variant="outline" onClick={() => mutate()}>
            Refetch
          </Button>
          {filters}
        </div>
      </div>
      <div className="flex-1 h-[calc(100vh-254px)]">
        <Card className="h-full flex flex-col">
          <CardContent className="flex-1 overflow-hidden pl-4 pr-0 pb-0">
            <div className="h-full overflow-auto">
              {data.items.length === 0 ? (
                <EmptyDatas />
              ) : (
                <Table table={table} />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default AvatarRedisLists
